
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Building, Users, ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { useMediaOwnerCheck } from "@/hooks/useMediaOwnerCheck";

interface DeviceMapFiltersProps {
  activeMediaKind: string;
  setActiveMediaKind: (kind: string) => void;
  activeScreenType: string;
  setActiveScreenType: (type: string) => void;
  activeMediaOwner: string;
  setActiveMediaOwner: (owner: string) => void;
}

export const DeviceMapFilters = ({
  activeMediaKind,
  setActiveMediaKind,
  activeScreenType,
  setActiveScreenType,
  activeMediaOwner,
  setActiveMediaOwner
}: DeviceMapFiltersProps) => {
  const { t } = useLanguage();
  const { isAdmin } = useAdminCheck();
  const { isMediaOwner, mediaOwnerName } = useMediaOwnerCheck();

  const showRotanaSigns = isAdmin || (isMediaOwner && mediaOwnerName === 'Rotana Signs');
  const showOnSpot = isAdmin || (isMediaOwner && mediaOwnerName === 'On Spot');
  const showWaveMedia = isAdmin || (isMediaOwner && mediaOwnerName === 'Wave Media');

  const getMediaOwnerLabel = () => {
    if (activeMediaOwner === 'all') return t("all");
    return activeMediaOwner;
  };
  
  return (
    <div className="flex flex-col sm:flex-row gap-4 mb-4">
      <div className="space-y-2">
        <span className="text-xs font-medium text-muted-foreground">{t("mediaOwner")}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className={`h-12 flex items-center justify-center gap-2 transition-all duration-200 bg-card dark:bg-card border-border/50 shadow-sm hover:shadow-md hover:scale-102 min-w-[140px]`}
            >
              <Users className="h-5 w-5" />
              <span className="font-medium">{getMediaOwnerLabel()}</span>
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-white dark:bg-gray-900 border-border/50 shadow-md w-[140px] z-[9999]">
            <DropdownMenuItem 
              className="gap-2 cursor-pointer hover:bg-accent focus:bg-accent" 
              onClick={() => setActiveMediaOwner('all')}
            >
              <Users className="h-4 w-4" />
              <span>{t("all")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="gap-2 cursor-pointer hover:bg-accent focus:bg-accent" 
              onClick={() => setActiveMediaOwner('Alan')}
            >
              <Users className="h-4 w-4" />
              <span>Alan</span>
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="gap-2 cursor-pointer hover:bg-accent focus:bg-accent" 
              onClick={() => setActiveMediaOwner('Waki')}
            >
              <Users className="h-4 w-4" />
              <span>Waki</span>
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="gap-2 cursor-pointer hover:bg-accent focus:bg-accent" 
              onClick={() => setActiveMediaOwner('Attention')}
            >
              <Users className="h-4 w-4" />
              <span>Attention</span>
            </DropdownMenuItem>
            {showRotanaSigns && (
              <DropdownMenuItem 
                className="gap-2 cursor-pointer hover:bg-accent focus:bg-accent" 
                onClick={() => setActiveMediaOwner('Rotana Signs')}
              >
                <Users className="h-4 w-4" />
                <span>Rotana Signs</span>
              </DropdownMenuItem>
            )}
            {showOnSpot && (
              <DropdownMenuItem 
                className="gap-2 cursor-pointer hover:bg-accent focus:bg-accent" 
                onClick={() => setActiveMediaOwner('On Spot')}
              >
                <Users className="h-4 w-4" />
                <span>On Spot</span>
              </DropdownMenuItem>
            )}
            {showWaveMedia && (
              <DropdownMenuItem 
                className="gap-2 cursor-pointer hover:bg-accent focus:bg-accent" 
                onClick={() => setActiveMediaOwner('Wave Media')}
              >
                <Users className="h-4 w-4" />
                <span>Wave Media</span>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      <div className="space-y-2">
        <span className="text-xs font-medium text-muted-foreground">{t("mediaType")}</span>
        <div className="flex gap-2">
          <Button
            variant={activeMediaKind === 'all' ? 'default' : 'outline'}
            onClick={() => setActiveMediaKind('all')}
            className={`flex-1 h-12 flex items-center justify-center gap-2 transition-all duration-200 ${
              activeMediaKind === 'all' 
                ? 'bg-primary text-white shadow-lg scale-105' 
                : 'bg-card dark:bg-card border-border/50 shadow-sm hover:shadow-md hover:scale-102'
            }`}
          >
            <Building className="h-5 w-5" />
            <span className="font-medium">{t("all")}</span>
          </Button>
          
          <Button
            variant={activeMediaKind === 'indoor' ? 'default' : 'outline'}
            onClick={() => setActiveMediaKind('indoor')}
            className={`flex-1 h-12 flex items-center justify-center gap-2 transition-all duration-200 ${
              activeMediaKind === 'indoor' 
                ? 'bg-primary text-white shadow-lg scale-105' 
                : 'bg-card dark:bg-card border-border/50 shadow-sm hover:shadow-md hover:scale-102'
            }`}
          >
            <Building className="h-5 w-5" />
            <span className="font-medium">{t("indoor")}</span>
          </Button>
          
          <Button
            variant={activeMediaKind === 'outdoor' ? 'default' : 'outline'}
            onClick={() => setActiveMediaKind('outdoor')}
            className={`flex-1 h-12 flex items-center justify-center gap-2 transition-all duration-200 ${
              activeMediaKind === 'outdoor' 
                ? 'bg-primary text-white shadow-lg scale-105' 
                : 'bg-card dark:bg-card border-border/50 shadow-sm hover:shadow-md hover:scale-102'
            }`}
          >
            <Building className="h-5 w-5" />
            <span className="font-medium">{t("outdoor")}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
